import "primeflex/primeflex.css";
import { Fieldset } from "primereact/fieldset";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Toast } from "primereact/toast";
import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CALC_TARIFF_POSTAL_SERVICES_LOAD, SET_STREET_ABBR } from "../../constants/actionTypes";
import DialogEditTerminal from "./components/DialogEditTerminal";
import DialogConfirm from "./components/DialogConfirm";
import SearchSelect from "../Calculator/SearchSelect";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import agent from "../../agent";
import styles from "./style.module.scss";

const initialFilters = {
  city: { kato: "" }
};

const initialPageConfig = {
  page: 0,
  pageSize: 10,
  totalElements: 0,
};

const Terminals = () => {
  const dispatch = useDispatch();
  const tableRef = useRef(null);
  const toast = useRef(null);

  const { postalServices } = useSelector((state) => state?.calcTariff);
  const { streetAbbrOptions } = useSelector((state) => state?.terminals);

  const [cities, setCities] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const [filters, setFilters] = useState(initialFilters);
  const [pageConfig, setPageConfig] = useState(initialPageConfig);
  const [terminals, setTerminals] = useState([]);

  /*
   Single modal state:
      - type: "edit" | "delete" | null
      - payload: terminal row (for edit/delete) OR null (create)
   */
  const [modal, setModal] = useState({
    type: null,
    payload: null
  });

  useAsyncEffect(async () => {
    await fetchPostalServices();
    await fetchStreetAbbr();
    await fetchCities("all", "");
    await fetchTerminals("", "", 0, initialPageConfig.pageSize);
  }, []);

  const showMessage = (
      severity, summary, detail, life = 5000, sticky = false
  ) => {
    toast.current?.show({ severity, summary, detail, life, sticky });
  };

  const handleFilters = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const fetchTerminals = async (
      id, kato, page = 0, pageSize = 10
  ) => {
    const data = await agent.Terminals
        .getTerminals(id || "", kato || "", page, pageSize);

    setPageConfig({
      page: data?.number ?? 0,
      pageSize: data?.size ?? pageSize,
      totalElements: data?.totalElements ?? 0,
    });

    setTerminals(data?.content || []);
  };

  const fetchStreetAbbr = async () => {
    const data = await agent.StreetAbbr.all();
    dispatch({ type: SET_STREET_ABBR, payload: data });
  };

  const fetchPostalServices = async () => {
    const postal = await agent.CalcTariff.getPostalServices();
    dispatch({ type: CALC_TARIFF_POSTAL_SERVICES_LOAD, payload: postal });
  };

  const fetchCities = async (type, value) => {
    const data = await agent.Terminals.searchCity(value);
    if (type === "all") {
      setCities(data);
      setCityOptions(data);
    } else if (type === "search") {
      setCityOptions(data);
    }
  };

  const handlePageChange = async (event) => {
    await fetchTerminals(filters?.cargo, filters?.city?.kato, event.page, event.rows);
  };

  const openCreate = () => setModal({ type: "edit", payload: null });
  const openEdit = (row) => setModal({ type: "edit", payload: row });
  const openDelete = (row) => setModal({ type: "delete", payload: row });
  const closeModal = () => setModal({ type: null, payload: null });

  const onDialogSuccess = async () => {
    closeModal();
    await fetchTerminals(filters?.cargo, filters?.city?.kato, pageConfig.page, pageConfig.pageSize);
  };

  const handleFind = async () => {
    await fetchTerminals(
        filters?.cargo,
        filters?.city?.kato,
        0,
        pageConfig.pageSize
    );
  };

  const handleClean = async () => {
    setFilters(initialFilters);
    setCityOptions(cities || []);
    await fetchTerminals("", "", 0, pageConfig.pageSize);
  };

  // Table body renderers
  const tableBodyPostServ = (rowData) => (
      <p>
        {postalServices
            ?.find((ps) => ps?.id === rowData?.postal_service_id)
            ?.nameRu
            || ""
        }
      </p>
  );

  const tableBodyStreetType = (rowData) => (
      <p>
        {streetAbbrOptions
            ?.find((abbr) => abbr?.id === rowData?.street_abbr_id)
            ?.nameRu
            || ""
        }
      </p>
  );

  const tableBodyCity = (rowData) => (
      <p>
        {cities
            ?.find((city) => city?.kato === rowData?.kato)
            ?.name
            || ""
        }
      </p>
  );

  const tableBodyButtons = (rowData) => (
      <div className="p-d-flex p-jc-center">
        <Button
            onClick={() => openEdit(rowData)}
            className="p-button-text p-button-rounded"
            icon="pi pi-pencil"
        />
        <Button
            onClick={() => openDelete(rowData)}
            className="p-button-text p-ml-3 p-button-rounded"
            icon="pi pi-trash"
        />
      </div>
  );

  return (
      <div>
        <Toast ref={toast} />

        <DialogConfirm
            viewDialog={modal.type === "delete"}
            hideDialog={closeModal}
            handleConfirm={onDialogSuccess}
            showMessage={showMessage}
            deleteTerminal={modal.type === "delete" ? modal.payload : null}
            cities={cities}
        />

        <DialogEditTerminal
            viewDialog={modal.type === "edit"}
            hideDialog={closeModal}
            cities={cities}
            streetAbbrOptions={streetAbbrOptions}
            postalServices={postalServices}
            editTerminal={modal.type === "edit" ? modal.payload : null}
            showMessage={showMessage}
            onSuccess={onDialogSuccess}
        />

        <div className="p-d-flex p-jc-between p-ai-center">
          <h2>Терминалы ГП/Кросс-докинг</h2>
          <Button
              className="p-d-block"
              label="Добавить терминал/кросс-докинг"
              onClick={openCreate}
              icon="pi pi-plus"
          />
        </div>

        <Fieldset legend="Header" toggleable>
          <div className="p-fluid p-formgrid p-grid p-mb-3">
            <div className="p-col-4 p-mr-3 p-d-flex p-flex-column">
              <label className="p-mb-1" htmlFor="city">Выберите город</label>
              <SearchSelect
                  placeholder=""
                  name="name"
                  state={filters.city}
                  setState={(e) => handleFilters("city", e)}
                  options={cityOptions}
                  optionValue={"kato"}
                  onChange={(e) => fetchCities("search", e)}
                  optionText={"name"}
              />
            </div>

            <div className="p-col-4 p-d-flex p-flex-column">
              <label className="p-mb-1" htmlFor="psId">Грузоперевозчик</label>
              <Dropdown inputId="psId" value={filters.cargo}
                        options={postalServices}
                        onChange={(e) => handleFilters('cargo', e.value)}
                        optionLabel="nameRu"
                        optionValue="id"
                        showClear
              />
            </div>
          </div>

          <div className="p-grid p-justify-end">
            <div className="p-mr-2">
              <Button className="p-button-warning"
                      label="Сбросить"
                      onClick={handleClean}
              />
            </div>
            <div className="p-mr-2">
              <Button onClick={handleFind} label="Найти" />
            </div>
          </div>
        </Fieldset>

        <div className="datatable-doc-demo">
          <div className="card">
            <div className="card">
              <DataTable
                  ref={tableRef}
                  value={terminals}
                  className={styles.terminalsTable}
                  dataKey="id"
                  rowHover
                  emptyMessage="Данные не найдены"
              >
                <Column
                    field="postal_service_id"
                    columnKey="postal_service_id"
                    header="Грузоперевозчик"
                    body={tableBodyPostServ}
                />
                <Column
                    field="kato"
                    columnKey="kato"
                    header="Город"
                    body={tableBodyCity}
                />
                <Column field="street" columnKey="street" header="Улица" />
                <Column
                    field="street_abbr_id"
                    columnKey="street_abbr_id"
                    header="Тип улицы"
                    body={tableBodyStreetType}
                />
                <Column field="house" columnKey="house" header="Дом" />
                <Column field="office" columnKey="office" header="Офис" />
                <Column field="minStorageDuration"
                        columnKey="minStorageDuration"
                        header="Срок хранения, мин"
                />
                <Column field="maxStorageDuration"
                        columnKey="maxStorageDuration"
                        header="Срок хранения, макс"
                />
                <Column field="time"
                        columnKey="time"
                        header="Время работы терминала"
                />
                <Column field="status" columnKey="status" header="Статус" />
                <Column field="id" header="Действия" body={tableBodyButtons} />
              </DataTable>
            </div>

            <Paginator
                currentPageReportTemplate="Показано с {first} по {last} из {totalRecords} записей"
                template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                first={pageConfig.page * pageConfig.pageSize}
                rows={pageConfig.pageSize}
                totalRecords={pageConfig.totalElements}
                rowsPerPageOptions={[10, 25, 50]}
                onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
  );
};

export default Terminals;